import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ModalDialog from '../ModalDialog';
import useMultiValidator from '../../hooks/useMultiValidator';
import TextField from '../fields/TextField';
import useAuthentication from '../../hooks/useAuthentication';
import {
    fourEyesPrinciplePasswordValidator,
    fourEyesPrincipleUserIdValidator,
} from '../../validators/fourEyesPrinciple';
import { getUserIdFromToken } from '../../utils/authentication';
import './_styles_/FourEyesPopup.css';
import PasswordField from '../fields/PasswordField';
import { extendedMutationType } from '../../types';
import Spinner from '../Spinner';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

FourEyesPopup.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    message: PropTypes.string.isRequired,
    extendedMutation: extendedMutationType.isRequired,
};

function FourEyesPopup(props) {
    const {
        onConfirm,
        onCancel,
        message,
        extendedMutation,
    } = props;
    const { t } = useTranslation();
    const { accessToken } = useAuthentication();
    const currentUserId = getUserIdFromToken(accessToken);
    const [userId, setUserId] = useState('');
    const [userPassword, setUserPassword] = useState('');
    const onConfirmHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        onConfirm(userId, userPassword);
    };
    const { validators, isValid } = useMultiValidator(false, {
        userId: fourEyesPrincipleUserIdValidator(t, currentUserId),
        password: compositeValidator(fourEyesPrinciplePasswordValidator(t), wafValidator(t)),
    });
    const [isDisabled, setIsDisabled] = useState(true);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const dialogMessage = t('four-eyes-principle.dialog-message');
    const onCancelInternal = () => {
        if (!extendedMutation.loading) {
            onCancel();
        }
    };
    return (
        <ModalDialog onCancel={onCancelInternal}>
            <div className="four-eyes-popup-dialog" role="dialog" aria-modal>
                <div className="message" aria-label={message}>{message}</div>
                <div className="dialog-message" aria-label={dialogMessage}>{dialogMessage}</div>
                <form className="four-eyes-credentials" onSubmit={onConfirmHandler}>
                    <div className="four-eyes-credentials-input-row">
                        <TextField
                            label={t('four-eyes-principle.user-id')}
                            inputId="four-eyes-credentials-user-id"
                            className="four-eyes-credentials-user-id"
                            value={userId}
                            validator={validators.userId}
                            onChange={setUserId}
                            autoComplete="username"
                            useDynamicWidth={false}
                            disabled={extendedMutation.loading}
                        />
                    </div>
                    <div className="four-eyes-credentials-input-row">
                        <PasswordField
                            label={t('four-eyes-principle.password')}
                            inputId="four-eyes-credentials-password"
                            className="four-eyes-credentials-password"
                            value={userPassword}
                            validator={validators.password}
                            onChange={setUserPassword}
                            disabled={extendedMutation.loading}
                        />
                    </div>
                    <div className="buttons">
                        {extendedMutation.loading ? (
                            <button
                                type="button"
                                className="confirm-four-eyes-button button-bordered button"
                                aria-label={t('four-eyes-principle.dialog-message')}
                                disabled
                            >
                                <Spinner size={10} displayLabel={false} className="four-eyes-confirm-button-loading" />
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="confirm-four-eyes-button button-filled-red button"
                                disabled={isDisabled}
                            >
                                {t('button.confirm')}
                            </button>
                        )}
                        <button
                            type="button"
                            className="cancel-four-eyes-button button-bordered button"
                            onClick={onCancelInternal}
                            disabled={extendedMutation.loading}
                        >
                            {t('button.cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </ModalDialog>
    );
}

export default FourEyesPopup;
